import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Logo, MonoLabel } from '../components/primitives/index.js';
import { PVDocPreview, ScanningOverlay } from '../components/composites/index.js';

export function ContestScan({ id }: { id: string }) {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();

  // Kick off the OCR (stubbed in Phase 1) once on mount.
  const extract = useMutation({
    mutationFn: () => api.extractContest(id),
  });

  const contest = useQuery({
    queryKey: ['contest', id],
    queryFn: () => api.contest(id),
    enabled: !!me.data,
    refetchInterval: (q) => {
      const status = q.state.data?.contest.status;
      if (status === 'ocr_pending') return 750;
      return false;
    },
  });

  useEffect(() => {
    if (me.data && extract.isIdle) extract.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data]);

  useEffect(() => {
    const status = contest.data?.contest.status;
    if (status === 'ocr_done') {
      const t = setTimeout(() => setLocation(`/contest/${id}/confirm`), 800);
      return () => clearTimeout(t);
    }
    if (status === 'ocr_failed') {
      const t = setTimeout(() => setLocation(`/contest/${id}/confirm`), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [contest.data?.contest.status, id, setLocation]);

  return (
    <div className="min-h-dvh bg-bg flex flex-col">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 py-8 md:py-12">
        <MonoLabel>Pasul 2 · AI citește PV-ul</MonoLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-display-4 font-medium mt-3 mb-6 md:mb-8 text-center max-w-xl">
          Extragem fapta, articolele și agentul…
        </h1>
        <div className="relative">
          <PVDocPreview scale={1.6} />
          <ScanningOverlay />
        </div>
        <div className="mt-10 text-sm text-muted text-center max-w-md">
          În 2–3 secunde citim seria PV-ului, data, locația, încadrarea juridică și sancțiunea.
        </div>
        {extract.isError && (
          <div className="mt-6 text-sm text-danger">
            Nu am putut citi PV-ul. Reîncearcă sau introdu manual datele.
          </div>
        )}
      </main>
    </div>
  );
}
